import { useState, useEffect, useCallback } from 'react';
import { ClientsTable } from '@/components/admin/tables/ClientsTable';
import { ClientDrawer } from '@/components/admin/drawers/ClientDrawer';
import { clientsService } from '@/services/admin/clientsService';
import type { ClientsListResponse, ClientFilter } from '@/types/admin';
import { AdminPageShell, PageHeader, PageError, PageLoading } from '@/components/admin/shell';

/**
 * ClientsPage - Full page with clients table and client drawer
 */
export function ClientsPage() {
  const [data, setData] = useState<ClientsListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filters and pagination
  const [filter, setFilter] = useState<ClientFilter>('all');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Selected items for drawer
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await clientsService.fetchClients({
        filter,
        search: search || undefined,
        page: currentPage,
        per_page: 20,
      });
      setData(result);
    } catch {
      setError('Error al cargar los clientes');
    } finally {
      setLoading(false);
    }
  }, [filter, search, currentPage]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const handleFilterChange = (newFilter: ClientFilter) => {
    setFilter(newFilter);
    setCurrentPage(1); // Reset to first page
  };

  const handleSearchChange = (newSearch: string) => {
    setSearch(newSearch);
    setCurrentPage(1); // Reset to first page
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleClientClick = (clientId: string) => {
    setSelectedClientId(clientId);
  };

  const handleCloseDrawer = () => {
    setSelectedClientId(null);
  };

  const handleCompanionClick = (userId: string) => {
    // Open the companion's drawer
    setSelectedClientId(userId);
  };

  return (
    <AdminPageShell>
      <div className="space-y-6">
        <PageHeader
          title="Clientes"
          subtitle="Gestiona y visualiza el historial de todos tus clientes"
        />

        {error && (
          <PageError message={error} onRetry={fetchClients} />
        )}

        {data && (
          <ClientsTable
            clients={data.clients}
            meta={data.meta}
            onClientClick={handleClientClick}
            onFilterChange={handleFilterChange}
            onSearchChange={handleSearchChange}
            onPageChange={handlePageChange}
            loading={loading}
          />
        )}

        {!data && loading && (
          <PageLoading variant="simple" />
        )}
      </div>

      <ClientDrawer
        userId={selectedClientId}
        open={selectedClientId !== null}
        onClose={handleCloseDrawer}
        onCompanionClick={handleCompanionClick}
      />
    </AdminPageShell>
  );
}
